import { gql, useMutation } from '@apollo/client';
import { IStackProps, PrimaryButton, Stack, TextField } from '@fluentui/react';
import { useEffect, useState } from 'react';
import Notification, { INotification } from '../../components/InlineNotification';
import { useTranslation } from 'react-i18next';
import { ReactComponent as BgImg } from './img/bg.svg';
import { ReactComponent as Logo } from './img/logo.svg';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';

const RESET_MUTATION = gql`
    mutation (
        $token: String,
        $password: String
    ) {
        reset(input: {Token: $token, Password: $password}) {
            Error
        }
    }
`;

const validatePassword = (pwd: string): string[] => {
    const errors: string[] = [];
    if (pwd.length < 9) errors.push('pwd-min-length');
    if (!/[A-Z]/.test(pwd)) errors.push('pwd-uppercase');
    if (!/[a-z]/.test(pwd)) errors.push('pwd-lowercase');
    if (!/[0-9]/.test(pwd)) errors.push('pwd-number');
    if (!/[-*?!@#$/(){}=.,;:]/.test(pwd)) errors.push('pwd-special');
    if (/[áéíóúÁÉÍÓÚñÑ]/.test(pwd)) errors.push('pwd-no-accents');
    return errors;
};

const textFieldStyles = {
    subComponentStyles: {
        label: {
            root: {
                selectors: {
                    '::after': {
                        color: '#ff4d4d',
                    },
                },
            },
        },
    },
};

const getErrorFieldStyles = (hasError: boolean) => ({
    ...textFieldStyles,
    fieldGroup: {
        borderColor: hasError ? '#ff4d4d' : undefined,
    },
    errorMessage: {
        color: '#ff4d4d',
        textShadow: '0 0 8px rgba(255, 77, 77, 0.4)',
        fontWeight: 'normal',
    },
});

const Login = ({ setToken }: { setToken: Function }) => {
    const { t } = useTranslation();
    const [errorReset, setErrorReset] = useState('');
    const [message, setMessage] = useState<{ type: INotification; text: string }>({ type: 'success', text: '' });

    const [formState, setFormState] = useState({
        password1: '',
        password2: '',
    });

    const [reset] = useMutation(RESET_MUTATION, {
        variables: {
            token: window.location.search.slice(7),
            password: formState.password1,
        },
        onCompleted: ({ reset }) => {
            if (!reset || (reset.Error && reset.Error !== '')) {
                setMessage({ type: 'error', text: reset.Error || 'Error-try-later' });
            } else {
                setMessage({ type: 'success', text: 'New-password-success' });
                setErrorReset('');
            }
        },
    });

    const handleReset = () => {
        const errors = validatePassword(formState.password1);
        if (errors.length > 0) {
            return;
        }
        if (formState.password1 !== formState.password2) {
            setErrorReset('The-password-does-not-coincide');
            return;
        }
        setErrorReset('');
        reset();
    };

    useEffect(() => {
        if (message.type !== 'error' && window.location.search.slice(0, 7) !== '?reset=') {
            setMessage({ type: 'error', text: 'Invalid-reset-link' });
        }
    }, [message.type, t]);

    const columnProps: Partial<IStackProps> = {
        tokens: { childrenGap: 15 },
        styles: { root: { width: 300 } },
    };

    return (
        <>
            <main id="login">
                <div className="login-panel"><BgImg /></div>
                <div className="login-form-container">
                    <form className="login-form">
                        <div className="logo"><Logo /></div>
                        <Stack {...columnProps}>
                            {message.text !== '' &&
                                <Notification
                                    text={t(message.text)}
                                    type={message.type}
                                    visible={true}
                                    isMultiline={true}
                                    toggleVisibility={() => { window.location.assign(window.location.origin); }}
                                    autoHide={false}
                                />
                            }
                            {message.text === '' &&
                                <>
                                    <TextField
                                        label={t('New-password-label')}
                                        type="password"
                                        autoComplete="new-password"
                                        canRevealPassword
                                        required
                                        styles={textFieldStyles}
                                        onChange={(e: any) => {
                                            const val = e.target.value;
                                            setFormState({ ...formState, password1: val });
                                        }}
                                    />

                                    {(() => {
                                        const errors = validatePassword(formState.password1);
                                        const isValid = formState.password1 !== '' && errors.length === 0;
                                        return (
                                            <ul style={{
                                                color: isValid ? '#a4e965' : '#ff4d4d',
                                                fontSize: 12,
                                                margin: 0,
                                                paddingLeft: 16,
                                                textShadow: isValid
                                                    ? '0 0 8px rgba(164, 233, 101, 0.4)'
                                                    : '0 0 8px rgba(255, 77, 77, 0.4)',
                                            }}>
                                                {isValid
                                                    ? <li>{t('pwd-valid')}</li>
                                                    : errors.length > 0
                                                        ? errors.map((key, i) => <li key={i}>{t(key)}</li>)
                                                        : validatePassword('').map((key, i) => <li key={i} style={{ opacity: 0.5 }}>{t(key)}</li>)
                                                }
                                            </ul>
                                        );
                                    })()}

                                    <TextField
                                        label={t('Repeat-new-password-label')}
                                        type="password"
                                        autoComplete="new-password"
                                        canRevealPassword
                                        required
                                        styles={getErrorFieldStyles(!!errorReset)}
                                        errorMessage={errorReset ? t(errorReset) : ''}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleReset();
                                            }
                                        }}
                                        onChange={(e: any) =>
                                            setFormState({ ...formState, password2: e.target.value })
                                        }
                                    />

                                    <PrimaryButton
                                        id="loginbtn"
                                        text={t('Set-new-password')}
                                        onClick={() => handleReset()}
                                        allowDisabledFocus
                                        disabled={validatePassword(formState.password1).length > 0}
                                        type="button"
                                    />
                                </>
                            }
                            <LanguageSelector />
                        </Stack>
                    </form>
                </div>
            </main>
        </>
    );
};

export default Login;